import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages an image lightbox with navigation.
 *
 * @extends DialogComponent
 */
export class ImageLightboxComponent extends DialogComponent {
  requiredRefs = ['dialog', 'prevButton', 'nextButton'];

  connectedCallback() {
    super.connectedCallback();
    this.#findLightboxImages();
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.addEventListener('dialog:close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.removeEventListener('dialog:close', this.#handleDialogClose);
    if (this.refs.dialog) {
      this.refs.dialog.removeEventListener('keydown', this.#handleKeyDown);
    }
  }

  /**
   * Finds all image blocks with lightbox enabled in the same section.
   */
  #findLightboxImages() {
    const section = this.closest('[id^="shopify-section-"]');
    if (!section) {
      this.lightboxImages = [];
      this.currentIndex = -1;
      return;
    }

    const allLightboxes = Array.from(
      section.querySelectorAll('image-lightbox-component.image-block-lightbox')
    );

    this.lightboxImages = allLightboxes;
    this.currentIndex = this.lightboxImages.indexOf(this);
  }

  /**
   * Updates navigation button visibility based on available images.
   */
  #updateNavigationButtons() {
    const { prevButton, nextButton } = this.refs;
    if (!prevButton || !nextButton) return;

    const hasMultiple = this.lightboxImages?.length > 1;

    if (hasMultiple && this.currentIndex > 0) {
      prevButton.style.display = 'flex';
    } else {
      prevButton.style.display = 'none';
    }

    if (hasMultiple && this.currentIndex < this.lightboxImages.length - 1) {
      nextButton.style.display = 'flex';
    } else {
      nextButton.style.display = 'none';
    }
  }

  /**
   * Handles dialog open event to update navigation buttons and load images.
   */
  #handleDialogOpen = () => {
    this.#findLightboxImages();
    this.#updateNavigationButtons();
    this.#loadLightboxImage();
    if (this.refs.dialog) {
      this.refs.dialog.addEventListener('keydown', this.#handleKeyDown);
    }
  };

  /**
   * Lazy loads the lightbox image when dialog opens.
   */
  loadLightboxImage() {
    const lightboxImage = this.refs.lightboxImage;
    if (!lightboxImage) return;

    // Only load if not already loaded
    if (lightboxImage.src) return;

    // Load the image
    if (lightboxImage.dataset.srcset) {
      lightboxImage.srcset = lightboxImage.dataset.srcset;
      lightboxImage.sizes = lightboxImage.dataset.sizes || '100vw';
    }
    if (lightboxImage.dataset.src) {
      lightboxImage.src = lightboxImage.dataset.src;
    }
  }

  /**
   * Private method wrapper for internal use.
   */
  #loadLightboxImage() {
    this.loadLightboxImage();
  }

  /**
   * Handles dialog close event to clean up.
   */
  #handleDialogClose = () => {
    if (this.refs.dialog) {
      this.refs.dialog.removeEventListener('keydown', this.#handleKeyDown);
    }
  };

  /**
   * Handles keyboard navigation (arrow keys).
   */
  #handleKeyDown = (event) => {
    // Don't interfere with Escape key handling from parent
    if (event.key === 'Escape') return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      this.navigatePrevious();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      this.navigateNext();
    }
  };

  /**
   * Navigates to the previous image in the lightbox gallery.
   */
  navigatePrevious = () => {
    if (this.currentIndex <= 0) return;

    const previousLightbox = this.lightboxImages[this.currentIndex - 1];
    if (!previousLightbox || !previousLightbox.showDialog) return;

    // Preload the image before opening
    if (previousLightbox.loadLightboxImage) {
      previousLightbox.loadLightboxImage();
    }

    this.closeDialog().then(() => {
      requestAnimationFrame(() => {
        previousLightbox.showDialog();
      });
    });
  };

  /**
   * Navigates to the next image in the lightbox gallery.
   */
  navigateNext = () => {
    if (this.currentIndex >= this.lightboxImages.length - 1) return;

    const nextLightbox = this.lightboxImages[this.currentIndex + 1];
    if (!nextLightbox || !nextLightbox.showDialog) return;

    // Preload the image before opening
    if (nextLightbox.loadLightboxImage) {
      nextLightbox.loadLightboxImage();
    }

    this.closeDialog().then(() => {
      requestAnimationFrame(() => {
        nextLightbox.showDialog();
      });
    });
  };
}

if (!customElements.get('image-lightbox-component'))
  customElements.define('image-lightbox-component', ImageLightboxComponent);

